import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useCan } from '@/app/casl/AbilityContext';
import { RESOURCES } from '@/app/casl/resources';
import { AdminAction } from '@/app/models/AdminAction';
import PageShell from '@/app/components/layout/PageShell';
import StatusBadge from '@/app/modules/table/components/StatusBadge';
import TaskProgressButton from '@/app/modules/task/components/TaskProgressButton';
import { getTaskStatus } from '@/app/modules/task/core/_requests';
import MetronicCard from '@/app/components/layout/MetronicCard';
import { CompanySelect, useActiveCompanies } from '@/app/components/selects/CompanySelect';
import DateInput from '@/app/components/inputs/DateInput';
import { canChooseCompany, chosenCompany } from '@/app/utils/companyChoice';
import { companyFilterFor } from '@/app/utils/companyFilter';

import { KTSVG as KtSvg } from '../../../_metronic/helpers';
import { DASHBOARD_PAGE_URL } from '../../../api/core/links';
import { AccountantManagementEndpoints } from '../../../api/endpoints/AccountantManagementEndpoints';
import DataTableBody from '../../modules/table/components/DataTableBody';
import DataTableControls from '../../modules/table/components/DataTableControls';
import DataTablePagination from '../../modules/table/components/DataTablePagination';
import { DataTable } from '../../modules/table/DataTable';
import EmptyNfeList from './components/EmptyNfeList';
import NfeStatusTabNav from './components/NfeStatusTabNav';
import SummaryCard from './components/SummaryCard';
import { generateInvoicesPdf, generateSpedFiscal, getAccountantDashboardMetrics } from './core/_requests';

import { type PageLink } from '../../../_metronic/layout/core';
import { type DataTableColumnDef } from '../../models/DataTableColumnDef';
import { type NfeDto } from '../../models/NfeDto';
import { type TaskInfoDto } from '@/app/models/TaskInfoDto';
import { type CompanyDto } from '@/app/models/CompanyDto';
import { type CompanyFilter } from '@/app/models/filters/CompanyFilter';
import { type FiscalSpedRequestDto } from '@/app/models/FiscalSpedRequestDto';

type FilterOption = {
  label: string;
  value: string;
};

type FiltersState = {
  invoiceType: string;
  period: string;
};

const breadcrumbs: Array<PageLink> = [
  {
    title: 'Dashboard',
    path: DASHBOARD_PAGE_URL,
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
  {
    title: 'Espaço Contador',
    path: '/espaco-contador',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
];

dayjs.locale('pt-br');

const buildPeriodOptions = (): FilterOption[] => {
  const now = dayjs();
  const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

  return Array.from({ length: 12 }, (_, index) => {
    const date = now.subtract(index, 'month');
    return {
      value: date.format('YYYY-MM'),
      label: capitalize(date.format('MMMM/YYYY')),
    };
  });
};

const periodOptions: FilterOption[] = buildPeriodOptions();

const invoiceTypeOptions: FilterOption[] = [
  { value: '', label: 'Todas' },
  { value: 'NFCE', label: 'Cupom fiscal eletrônico' },
  { value: 'NFE_IN', label: 'Nota fiscal de entrada' },
  { value: 'NFE_OUT', label: 'Nota fiscal de saída' },
];

const formatPercent = (value: number) => `${value.toFixed(2)}%`;

const formatCurrencyCompact = (value: number) => {
  if (value >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `R$ ${(value / 1_000).toFixed(1)}K`;
  }
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(value);
};

const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value);

const previousMonthComparison = (delta: number, fractionDigits: number) => {
  const percent = Number((delta * 100).toFixed(fractionDigits));
  const isDrop = percent < 0;
  const sign = isDrop ? '↓ -' : '↑ +';
  return {
    helper: `${sign}${Math.abs(percent).toFixed(fractionDigits)}% vs mês anterior`,
    helperColor: isDrop ? 'danger' : 'success',
  };
};

const formatCurrencyBRL = (value?: number) => {
  if (value == null) {
    return '-';
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value);
};

const columns: DataTableColumnDef<NfeDto>[] = [
  {
    id: 'number',
    header: 'Número',
    accessorKey: 'number',

    cell: ({ row }) => <span className="text-gray-800 fw-bold">{row.original.number || '-'}</span>,
  },
  {
    id: 'series_number',
    header: 'Série',
    accessorKey: 'series_number',

    cell: ({ row }) => <span className="text-gray-600 fw-semibold">{row.original.series_number || '-'}</span>,
  },
  {
    id: 'type',
    header: 'Tipo',
    accessorKey: 'type.value',

    enableSorting: false,
    cell: ({ row }) => {
      const type = row.original.type;
      const label = type?.description || '--';

      const badge = () => {
        switch (type?.value) {
          case 'NFE_IN':
            return 'badge badge-light-info';
          case 'NFE_OUT':
            return 'badge badge-light-primary';
          case 'NFCE':
            return 'badge badge-light-success';
          default:
            return 'badge badge-light-secondary';
        }
      };

      return <span className={badge()}>{label}</span>;
    },
  },
  {
    id: 'receiver_name',
    header: 'Destinatário',
    accessorKey: 'receiver_name',

    cell: ({ row }) => <span className="text-gray-800 fw-semibold">{row.original.receiver_name || '-'}</span>,
  },
  {
    id: 'receiver_cnpj',
    header: 'CNPJ',
    accessorKey: 'receiver_cnpj',

    cell: ({ row }) => <span className="text-gray-600 fw-semibold">{row.original.receiver_cnpj || '-'}</span>,
  },
  {
    id: 'due_date',
    header: 'Data Emissão',
    accessorKey: 'due_date',

    cell: ({ row }) => {
      const date = row.original.due_date;
      return <span className="text-gray-700 fw-semibold">{date ? dayjs(date).format('DD/MM/YYYY') : '-'}</span>;
    },
  },
  {
    id: 'total',
    header: 'Valor',
    accessorKey: 'total',

    cell: ({ row }) => <span className="text-gray-800 fw-bold">{formatCurrencyBRL(row.original.total)}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status.value',

    cell: ({ row }) => {
      const status = row.original.status;
      const value = status?.value;

      const getStatusBadge = () => {
        switch (value) {
          case 'PENDING':
            return <StatusBadge label="Pendente" tone="warning" />;
          case 'ISSUED':
            return <StatusBadge label="Emitida" tone="primary" />;
          case 'AUTHORIZED':
            return <StatusBadge label="Autorizada" tone="success" />;
          case 'DENIED':
            return <StatusBadge label="Negada" tone="info" />;
          case 'CANCELED':
          case 'CANCELED_DENIED':
            return <StatusBadge label="Cancelada" tone="danger" />;
          default:
            return <StatusBadge label={status?.description || value || '-'} tone="secondary" />;
        }
      };

      return getStatusBadge();
    },
  },
  {
    id: 'actions',
    header: 'Ações',

    cell: ({ row }) => {
      const xmlUrl = row.original.link_download_xml;
      const danfeUrl = row.original.link_download_danfe;

      const handleOpen = (url?: string) => {
        if (!url) return;
        window.open(url, '_blank', 'noopener,noreferrer');
      };

      return (
        <div className="d-flex gap-2">
          <button
            className="btn btn-icon btn-light-secondary btn-sm"
            disabled={!danfeUrl}
            title="Baixar PDF"
            type="button"
            onClick={() => handleOpen(danfeUrl)}
          >
            <KtSvg className="svg-icon-2" path="media/svg/files/pdf.svg" />
            <span className="visually-hidden">Baixar PDF</span>
          </button>
          <button
            className="btn btn-icon btn-light-primary btn-sm"
            disabled={!xmlUrl}
            title="Baixar XML"
            type="button"
            onClick={() => handleOpen(xmlUrl)}
          >
            <KtSvg className="svg-icon-2" path="media/svg/files/xml.svg" />
            <span className="visually-hidden">Baixar XML</span>
          </button>
        </div>
      );
    },
  },
];
function AccountantSpaceDashboardPage() {
  const canExport = useCan(RESOURCES.ESPACO_CONTADOR_DASHBOARD, AdminAction.EXPORTAR);

  const [filters, setFilters] = useState<FiltersState>({
    invoiceType: invoiceTypeOptions[0].value,
    period: periodOptions[0].value,
  });
  const nfeType = filters.invoiceType || undefined;

  // Vazio mostra todas as empresas; o SPED é de uma empresa só e sai da escolhida aqui.
  const { data: companies = [] } = useActiveCompanies();
  const [selectedCompany, setSelectedCompany] = useState<CompanyDto | null>(null);
  const spedCompany = chosenCompany(companies, selectedCompany);
  const [inventoryDate, setInventoryDate] = useState('');
  const spedRequest: FiscalSpedRequestDto = {
    period: filters.period,
    company: spedCompany,
    inventory_date: inventoryDate || undefined,
  };
  const companyFilter: CompanyFilter = selectedCompany ? companyFilterFor([selectedCompany]) : {};

  const {
    data: metricsResponse,
    isFetching,
    isLoading,
  } = useQuery({
    queryKey: ['accountant-dashboard-metrics', filters.period, filters.invoiceType, companyFilter.company_ids],

    queryFn: () =>
      getAccountantDashboardMetrics({
        period: filters.period,
        nfe_type: nfeType,
        nfe_status: 'all',
        company_ids: companyFilter.company_ids,
      }),

    placeholderData: keepPreviousData,
  });
  const isRefreshingMetrics = isFetching && !isLoading;

  const { data: spedTaskInfo } = useQuery<TaskInfoDto<string>>({
    queryKey: ['download-sped-fiscal-task-info'],
    queryFn: async () => {
      const response = await getTaskStatus('download-sped-fiscal-task');
      return response.data;
    },
    retry: false,
    meta: { suppressGlobalError: true },
  });

  const { data: invoicesPdfTaskInfo } = useQuery<TaskInfoDto<string>>({
    queryKey: ['download-nfe-task-info'],
    queryFn: async () => {
      const response = await getTaskStatus('download-nfe-task');
      return response.data;
    },
    retry: false,
    meta: { suppressGlobalError: true },
  });

  const handleTaskSuccess = (task: TaskInfoDto<string>) => {
    const downloadUrl = typeof task.result === 'string' ? task.result : '';
    if (downloadUrl) {
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const metrics = useMemo(() => {
    const payload = metricsResponse?.data;

    const issued = payload?.total_invoices ?? 0;
    const success = payload?.approved_invoices ?? 0;
    const error = payload?.rejected_invoices ?? 0;
    const totalValue = payload?.total_amount ?? 0;
    const prevIssued = payload?.total_invoices_previous_period ?? 0;
    const prevValue = payload?.total_amount_previous_period ?? 0;

    const issuedDelta = prevIssued > 0 ? (issued - prevIssued) / prevIssued : 0;
    const valueDelta = prevValue > 0 ? (totalValue - prevValue) / prevValue : 0;

    return { issued, success, error, totalValue, issuedDelta, valueDelta };
  }, [metricsResponse?.data]);

  const successRate = useMemo(() => {
    if (!metrics.issued) {
      return 0;
    }
    return ((metrics.issued - metrics.error) / metrics.issued) * 100;
  }, [metrics.error, metrics.issued]);

  const errorRate = useMemo(() => {
    if (!metrics.issued) {
      return 0;
    }
    return (metrics.error / metrics.issued) * 100;
  }, [metrics.error, metrics.issued]);

  const currentPeriodLabel = useMemo(() => {
    return periodOptions.find((option) => option.value === filters.period)?.label || '';
  }, [filters.period]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'issued',
        title: 'Notas Emitidas',
        value: formatNumber(metrics.issued),
        icon: 'media/icons/duotune/files/fil009.svg',
        theme: 'primary',
        ...previousMonthComparison(metrics.issuedDelta, 0),
      },
      {
        id: 'success',
        title: 'Sucesso',
        value: formatNumber(metrics.success),
        helper: `${formatPercent(successRate)} de sucesso`,
        icon: 'media/icons/duotune/general/gen056.svg',
        theme: 'success',
        helperColor: 'success',
      },
      {
        id: 'error',
        title: 'Negada',
        value: formatNumber(metrics.error),
        helper: `${formatPercent(errorRate)} do total`,
        icon: 'media/icons/duotune/general/gen050.svg',
        theme: 'danger',
        helperColor: 'danger',
      },
      {
        id: 'value',
        title: 'Valor Total',
        value: formatCurrencyCompact(metrics.totalValue),
        icon: 'media/icons/duotune/general/gen016.svg',
        theme: 'dark',
        ...previousMonthComparison(metrics.valueDelta, 1),
      },
    ],
    [
      errorRate,
      metrics.error,
      metrics.issued,
      metrics.success,
      metrics.issuedDelta,
      metrics.totalValue,
      metrics.valueDelta,
      successRate,
    ],
  );

  const handleFilterChange = (key: keyof FiltersState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const renderFilterSelect = (label: string, key: keyof FiltersState, options: FilterOption[]) => (
    <div className="col-6">
      <label className="form-label fs-6 fw-semibold text-gray-700">{label}</label>
      <select
        className="form-select form-select-solid"
        value={filters[key]}
        onChange={(event) => handleFilterChange(key, event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <PageShell breadcrumbs={breadcrumbs} title="Dashboard">
      <div className="row g-4 mb-8">
        <div className="col-12 col-xl-8">
          <MetronicCard className="card-flush shadow-sm h-100" loading={isRefreshingMetrics}>
            <div className="card-body py-6">
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-6 gap-4">
                <div>
                  <h3 className="fw-bold text-gray-800 mb-1">Filtros de visualização</h3>
                  <div className="text-gray-500 fw-semibold">Ajuste os filtros para acompanhar as notas do período</div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span className="text-gray-600 fw-semibold">Período atual:</span>
                  <span className="badge badge-light-primary fs-base px-4 py-2">{currentPeriodLabel}</span>
                </div>
              </div>

              <div className="row g-4">
                {canChooseCompany(companies) ? (
                  <div className="col-12">
                    <label className="form-label fs-6 fw-semibold text-gray-700" htmlFor="accountant-company">
                      Empresa
                    </label>
                    <CompanySelect
                      isClearable
                      className="react-select-styled react-select-solid"
                      inputId="accountant-company"
                      value={selectedCompany}
                      onChange={setSelectedCompany}
                    />
                  </div>
                ) : null}
                {renderFilterSelect('Período', 'period', periodOptions)}
                {renderFilterSelect('Tipo de Nota', 'invoiceType', invoiceTypeOptions)}
              </div>
            </div>
          </MetronicCard>
        </div>

        <div className="col-12 col-xl-4">
          <MetronicCard className="card-flush shadow-sm h-100" loading={isRefreshingMetrics}>
            <div className="card-body py-6 d-flex flex-column gap-4">
              <div>
                <h3 className="fw-bold text-gray-800 mb-1">Downloads rápidos</h3>
                <div className="text-gray-500 fw-semibold">Acesso rápido aos arquivos fiscais</div>
              </div>
              {canChooseCompany(companies) && !selectedCompany ? (
                <div className="text-gray-500 fs-7">Escolha a empresa no filtro para baixar o SPED Fiscal.</div>
              ) : null}
              <div>
                <label className="form-label fs-6 fw-semibold text-gray-700" htmlFor="sped-inventory-date">
                  Data do inventário
                </label>
                <DateInput
                  className="form-control form-control-solid"
                  id="sped-inventory-date"
                  value={inventoryDate}
                  onChange={setInventoryDate}
                />
                <div className="form-text">
                  Preenche o Bloco H do SPED Fiscal com o estoque dessa data. Sem ela, o Bloco H sai sem movimento.
                </div>
              </div>
              <div className="d-flex flex-row gap-3">
                {canExport ? (
                  <TaskProgressButton
                    buttonClassName="btn btn-light-primary d-flex flex-column align-items-center py-4"
                    disabled={!spedCompany}
                    errorMessage="Falha ao gerar SPED Fiscal"
                    iconPath="media/icons/duotune/files/fil012.svg"
                    label="Baixar SPED Fiscal"
                    runningLabel="Gerando SPED..."
                    startTask={() => generateSpedFiscal(spedRequest)}
                    successMessage="SPED Fiscal gerado com sucesso"
                    taskInfo={spedTaskInfo}
                    onSuccess={handleTaskSuccess}
                  />
                ) : null}
                {canExport ? (
                  <TaskProgressButton
                    buttonClassName="btn btn-light-secondary d-flex flex-column align-items-center py-4"
                    errorMessage="Falha ao gerar PDF das notas"
                    iconPath="media/svg/files/pdf.svg"
                    label="Baixar todas as notas"
                    runningLabel="Gerando notas..."
                    startTask={() =>
                      generateInvoicesPdf({
                        period: filters.period,
                        nfe_type: nfeType,
                        nfe_status: 'all',
                        company_ids: companyFilter.company_ids,
                      })
                    }
                    successMessage="PDF das notas gerado com sucesso"
                    taskInfo={invoicesPdfTaskInfo}
                    onSuccess={handleTaskSuccess}
                  />
                ) : null}
              </div>
            </div>
          </MetronicCard>
        </div>
      </div>

      <div className="row g-6 mb-8">
        {summaryCards.map((card) => (
          <div key={card.id} className="col-12 col-md-6 col-xl-3">
            <SummaryCard
              helper={card.helper}
              helperColor={card.helperColor}
              icon={card.icon}
              initialLoading={isLoading}
              loading={isRefreshingMetrics}
              theme={card.theme}
              title={card.title}
              value={card.value}
            />
          </div>
        ))}
      </div>

      <div className="row mb-8">
        <div className="col-12">
          <MetronicCard className="card-flush" loading={isRefreshingMetrics}>
            <div className="card-body py-10">
              <DataTable
                columns={columns}
                direction="desc"
                endpoint={AccountantManagementEndpoints.listInvoices}
                exportEndpoint={AccountantManagementEndpoints.invoicesExport}
                exportFilterLabels={{ company_labels: 'Empresa' }}
                filter={{
                  nfe_type: nfeType,
                  period: filters.period,
                  ...companyFilter,
                }}
                isExportable={canExport}
                isRefreshable={true}
                placeHolder={<EmptyNfeList />}
                searchPlaceHolder="Buscar pelo fornecedor ou Nº da nota"
                sort="number"
                tableKey="nfe-accountant-dashboard-table"
                variant="list"
              >
                <DataTableControls />
                <NfeStatusTabNav />
                <DataTableBody showHeader={true} />
                <DataTablePagination />
              </DataTable>
            </div>
          </MetronicCard>
        </div>
      </div>
    </PageShell>
  );
}

export default AccountantSpaceDashboardPage;
